import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/shared/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
  {
    variants: {
      variant: {
        primary: 'bg-cta-gradient text-canvas shadow-fab hover:opacity-90 active:opacity-80',
        secondary: 'bg-surface-high text-on-surface hover:bg-surface-highest',
        ghost: 'bg-transparent text-on-surface hover:bg-surface-high',
        destructive: 'bg-danger text-on-surface hover:opacity-90',
      },
      size: {
        sm: 'h-11 px-4 text-label-md',
        md: 'h-12 px-5 text-label-lg',
        lg: 'h-14 px-6 text-body-lg',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  testID?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    fullWidth,
    asChild = false,
    loading,
    disabled,
    children,
    testID,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  // Quando `asChild`, o Slot aceita exatamente um filho — embutir o spinner
  // ao lado quebraria `Children.only`. Nesse modo, o consumidor controla
  // o conteudo (loading nao se aplica).
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      disabled={disabled ?? loading}
      data-testid={testID}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});
